/**
 * InboxPage — Startseite der ScanFlow-PWA (Belegübersicht).
 *
 * UI portiert aus dem Vimaana-Garten-Design ("Scanflow Startseite.html",
 * Variante 3 · Typ-Segmente + Zeitleiste). Aufbau:
 *   • Kopf (Datum + ScanFlow + Konto)
 *   • „Beleg erfassen"-CTA
 *   • Segmentleiste: Alle · Rechnungen · Helfer (Lieferscheine gehören ins
 *     Projekt und tauchen nach dem Scannen hier nicht wieder auf — nur als
 *     Entwurf, solange sie noch nicht abgeschlossen sind; 25.09.2026)
 *   • Suchzeile + ausklappbarer Filter (Erfasser / Projekt / Schnellfilter)
 *   • Liste nach Zeitraum gruppiert (Entwürfe · Diese Woche · Letzte Woche · Früher)
 *
 * Verdrahtet mit echten Belegen (api.listBelege) und Scannern (api.listScanner).
 * Entwürfe bleiben oben sichtbar; Tap auf eine Zeile öffnet den Beleg.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useBelegListe } from "../lib/useBelegListe";
import ListenEnde from "../components/ListenEnde";
import { api, istEntwurfStatus, type Beleg, type DatevSendResult } from "../lib/api";
import "./InboxPage.css";

// ───────────────────────── Icons (aus dem Design) ─────────────────────────
type IconName =
  | "camera" | "account" | "search" | "receipt" | "truck" | "briefcase"
  | "person" | "funnel" | "spark" | "x";

function SFIcon({ name }: { name: IconName }) {
  const paths: Record<IconName, React.ReactNode> = {
    camera: (<><path d="M4 8.5h3l1.6-2.4h6.8L17 8.5h3a1 1 0 0 1 1 1V18a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9.5a1 1 0 0 1 1-1z" /><circle cx="12" cy="13" r="3.6" /></>),
    account: (<><circle cx="12" cy="8.4" r="3.6" /><path d="M5.2 19.5a7 7 0 0 1 13.6 0" /></>),
    search: (<><circle cx="11" cy="11" r="7" /><path d="M16.5 16.5L21 21" /></>),
    receipt: (<><path d="M6 3.2h12v17.6l-2-1.4-2 1.4-2-1.4-2 1.4-2-1.4-2 1.4z" /><path d="M9 8h6M9 11.5h6" /></>),
    truck: (<><path d="M3 6.5h10v9H3z" /><path d="M13 9.5h4l3 3v3h-7z" /><circle cx="7" cy="17.5" r="1.7" /><circle cx="17" cy="17.5" r="1.7" /></>),
    briefcase: (<><rect x="3.5" y="7.5" width="17" height="12" rx="2.5" /><path d="M9 7.5V6a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v1.5" /><path d="M3.5 12h17" /></>),
    person: (<><circle cx="12" cy="8.2" r="3.5" /><path d="M5 20a7 7 0 0 1 14 0" /></>),
    funnel: <path d="M3.5 5h17l-6.5 7.5V19l-4 2v-8.5z" />,
    spark: <path d="M12 3l1.6 4.8L18 9.4l-4.4 1.6L12 16l-1.6-5L6 9.4l4.4-1.6z" />,
    x: <path d="M6 6l12 12M18 6L6 18" />,
  };
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7}
         strokeLinecap="round" strokeLinejoin="round">
      {paths[name]}
    </svg>
  );
}

// ───────────────────────── Beleg-Modell ─────────────────────────
type TypKey = "beleg" | "lieferschein" | "betrieb" | "helfer";
const TYP_META: Record<TypKey, { kurz: string; icon: IconName; tone: string }> = {
  beleg: { kurz: "Projekt", icon: "receipt", tone: "blue" },
  lieferschein: { kurz: "Lieferschein", icon: "truck", tone: "amber" },
  betrieb: { kurz: "Betrieb", icon: "briefcase", tone: "purple" },
  helfer: { kurz: "Helfer-Rg.", icon: "person", tone: "green" },
};

function typKey(b: Beleg): TypKey {
  if (b.beleg_typ === "lieferschein") return "lieferschein";
  if (b.ausgabe_typ === "helfer") return "helfer";
  if (b.ausgabe_typ === "betrieb") return "betrieb";
  return "beleg";
}

/** Beleg ohne erkannten Betrag (für Lieferscheine irrelevant — die haben Mengen). */
function fehltBetrag(b: Beleg): boolean {
  return (b.netto ?? 0) <= 0 && (b.bruttobetrag ?? 0) <= 0;
}
function istUnklassifiziert(b: Beleg): boolean {
  return typKey(b) !== "lieferschein" && !(b.ausgabe_typ || "").trim();
}
// ── Kontozuordnung / Verarbeitungs-Status (fürs Vorverarbeiten) ──────────────
type FilterFlag = "fehlt" | "unklass" | "ohne_konto" | "unbezahlt";
type SortKey = "neueste" | "datum" | "betrag" | "status";
type SegKey = "alle" | "beleg" | "lieferschein" | "helfer";

// Filter/Sortierung überleben den Wechsel in die Detailansicht und zurück —
// die Auswahl gehört dem Nutzer, sie wird nicht bei jeder Navigation verworfen.
const FILTER_KEY = "scanflow-inbox-filter";
type StoredFilter = {
  seg?: SegKey;
  q?: string;
  erfasser?: string[];
  projektSel?: string;
  flags?: FilterFlag[];
  sortBy?: SortKey;
  showDrafts?: boolean;
};
function loadFilter(): StoredFilter {
  try {
    return JSON.parse(sessionStorage.getItem(FILTER_KEY) || "{}") as StoredFilter;
  } catch {
    return {};
  }
}

/** Beleg braucht keine Bankzeile mehr: er hängt an einer, ist bar gezahlt oder privat
 *  ausgelegt. Privat ausgelegt lief nie über das GbR-Konto — er wäre sonst für immer
 *  „offen" (Benjamin, 03.11.2026, Nestler-Beleg). */
function hatKonto(b: Beleg): boolean {
  return !!b.kontobewegung_id || b.zahlungsart === "bar" || b.zahlungsweg === "inhaber";
}
/** Noch nicht an den Steuerberater/DATEV versendet. */
function nichtAnDatev(b: Beleg): boolean {
  return !b.an_steuerberater_versendet_at;
}
/** Unbezahlt = weder als bezahlt markiert noch bar oder privat ausgelegt (gilt als beglichen). */
function istUnbezahlt(b: Beleg): boolean {
  return !b.is_bezahlt && b.zahlungsart !== "bar" && b.zahlungsweg !== "inhaber";
}
/** Numerischer Rang für die Status-Sortierung (offen zuerst). */
function statusRank(b: Beleg): number {
  if (typKey(b) !== "lieferschein" && fehltBetrag(b)) return 0; // Betrag fehlt
  if (istUnklassifiziert(b)) return 1; // unklassifiziert
  if (!hatKonto(b)) return 2; // ohne Kontozuordnung
  if (nichtAnDatev(b)) return 3; // noch nicht an DATEV
  return 4; // fertig
}
function belegBetrag(b: Beleg): number {
  return (b.bruttobetrag ?? 0) > 0 ? b.bruttobetrag : (b.netto ?? 0);
}

/** Durchsuchbarer Text einer Rechnung — bewusst inkl. Datum (ISO **und** deutsch) und
 *  Betrag, damit man auch nach „1970", „12.01.1970", „35" oder „35,00" suchen kann. */
function searchText(b: Beleg, anzeige: string): string {
  const betrag = belegBetrag(b);
  const iso = b.datum || "";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  const de = m ? `${m[3]}.${m[2]}.${m[1]}` : "";
  const betragTxt = betrag > 0 ? betrag.toFixed(2) : "";
  return [
    anzeige,
    b.lieferant,
    b.projekt_title,
    b.notiz,
    b.ki_beschreibung,
    b.beleg_typ,
    iso,
    de,
    betragTxt,
    betragTxt.replace(".", ","),
  ]
    .join(" ")
    .toLowerCase();
}
function erfasserKey(b: Beleg): string {
  return b.source === "mail" ? "mail" : b.created_by_user_id || "";
}
function initials(name: string): string {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
function firstName(name: string): string {
  return (name || "").trim().split(/\s+/)[0] || name || "?";
}
function fmtEuro(n: number): string {
  return n.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
}
function bucketOf(datum: string): "Diese Woche" | "Letzte Woche" | "Früher" {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(datum || "");
  if (!m) return "Früher";
  const d = new Date(+m[1], +m[2] - 1, +m[3]);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = Math.floor((today.getTime() - d.getTime()) / 86_400_000);
  if (diff <= 7) return "Diese Woche";
  if (diff <= 14) return "Letzte Woche";
  return "Früher";
}

export default function InboxPage() {
  const nav = useNavigate();

  const scannerQ = useQuery({ queryKey: ["scanflow-scanner"], queryFn: () => api.listScanner() });
  const liefQ = useQuery({ queryKey: ["scanflow-lieferanten"], queryFn: () => api.listLieferanten() });

  const [seg, setSeg] = useState<SegKey>(() => {
    const s = loadFilter().seg ?? "alle";
    return s === "lieferschein" ? "alle" : s;
  });
  const [q, setQ] = useState(() => loadFilter().q ?? "");
  // Seitenweise nachladen; ab zwei Zeichen sucht der Server über alle Belege.
  const belegeQ = useBelegListe(q);
  const [filterOpen, setFilterOpen] = useState(false);
  const [erfasserSel, setErfasserSel] = useState<Set<string>>(
    () => new Set(loadFilter().erfasser ?? []),
  );
  const [projektSel, setProjektSel] = useState<string>(() => loadFilter().projektSel ?? "alle");
  // Mehrere Schnellfilter gleichzeitig (fürs Vorverarbeiten): Betrag fehlt,
  // Unklassifiziert, ohne Kontozuordnung, noch nicht an DATEV, unbezahlt.
  const [flags, setFlags] = useState<Set<FilterFlag>>(() => new Set(loadFilter().flags ?? []));
  // Sortierung: neueste (Erfassung), Belegdatum, Betrag, Status.
  const [sortBy, setSortBy] = useState<SortKey>(() => loadFilter().sortBy ?? "neueste");
  const [showDrafts, setShowDrafts] = useState(() => loadFilter().showDrafts ?? false);

  // Auswahl merken, damit sie beim Zurückkommen aus einem Beleg erhalten bleibt.
  useEffect(() => {
    const data: StoredFilter = {
      seg,
      q,
      erfasser: [...erfasserSel],
      projektSel,
      flags: [...flags],
      sortBy,
      showDrafts,
    };
    try {
      sessionStorage.setItem(FILTER_KEY, JSON.stringify(data));
    } catch {
      /* sessionStorage nicht verfügbar — Filter bleibt dann nur für diese Ansicht */
    }
  }, [seg, q, erfasserSel, projektSel, flags, sortBy, showDrafts]);

  // Filterpanel schließt sich, sobald man außerhalb (Panel + Filter-Button) tippt.
  const stickyRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!filterOpen) return;
    const onDown = (e: MouseEvent | TouchEvent) => {
      if (stickyRef.current && !stickyRef.current.contains(e.target as Node)) {
        setFilterOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
    };
  }, [filterOpen]);

  const firmaName = useMemo(() => {
    const m = new Map<string, string>();
    (liefQ.data ?? []).forEach((l) => m.set(l.id, l.name));
    return m;
  }, [liefQ.data]);

  const anzeigeName = (b: Beleg) =>
    (b.lieferant_firma_id ? firmaName.get(b.lieferant_firma_id) : "") ||
    b.lieferant ||
    (b.ki_laeuft ? "Neuer Beleg" : "—");

  // Erfasser-Initialen + Vornamen aus der Scanner-Liste
  const scannerMap = useMemo(() => {
    const m = new Map<string, string>();
    (scannerQ.data?.scanner ?? []).forEach((s) => m.set(s.id, s.name));
    if (scannerQ.data?.me?.id) m.set(scannerQ.data.me.id, scannerQ.data.me.name);
    return m;
  }, [scannerQ.data]);
  const erfasserIni = (key: string) => (key === "mail" ? "@" : initials(scannerMap.get(key) || ""));

  const alleBelege = belegeQ.belege;
  const drafts = alleBelege.filter((b) => istEntwurfStatus(b.status));
  // Gerade gescannt (letzte 2 Stunden, noch Entwurf) — steht oben in der Liste,
  // damit der eben gesicherte Beleg nicht hinter dem Entwurfs-Hinweis
  // verschwindet, während Clara ihn liest (16.10.2026).
  const frisch = drafts
    .filter(
      (b) =>
        b.ki_laeuft ||
        (b.source === "scanflow" && Date.now() - Date.parse(b.created_at || "") < 2 * 3600 * 1000),
    )
    .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
  const rest = alleBelege.filter((b) => !istEntwurfStatus(b.status) && b.beleg_typ !== "lieferschein");

  const term = q.trim().toLowerCase();
  // Hat der Server schon gesucht, nicht noch einmal in der App filtern — er kennt
  // mehr Felder (z. B. Rechnungsnummer), die App würde Treffer wieder wegwerfen.
  const matchSuche = (b: Beleg) =>
    !term || belegeQ.serverSuche || searchText(b, anzeigeName(b)).includes(term);

  const matchSeg = (b: Beleg) => {
    const t = typKey(b);
    if (seg === "alle") return true;
    // „Rechnungen" umfasst projektbezogene Rechnungen UND Betriebsausgaben (mit/ohne Projekt).
    if (seg === "beleg") return t === "beleg" || t === "betrieb";
    if (seg === "lieferschein") return t === "lieferschein";
    return t === "helfer"; // Helfer explizit
  };
  const matchFilter = (b: Beleg) => {
    if (erfasserSel.size && !erfasserSel.has(erfasserKey(b))) return false;
    if (projektSel !== "alle" && b.projekt_id !== projektSel) return false;
    if (flags.has("fehlt") && !(typKey(b) !== "lieferschein" && fehltBetrag(b))) return false;
    if (flags.has("unklass") && !istUnklassifiziert(b)) return false;
    if (flags.has("ohne_konto") && hatKonto(b)) return false;
    if (flags.has("unbezahlt") && !istUnbezahlt(b)) return false;
    return true;
  };

  // Sortier-Vergleich je nach gewählter Sortierung.
  const sortCmp = (a: Beleg, b: Beleg): number => {
    switch (sortBy) {
      case "betrag":
        return belegBetrag(b) - belegBetrag(a);
      case "status":
        return statusRank(a) - statusRank(b) || (b.created_at || "").localeCompare(a.created_at || "");
      case "datum":
        return (b.datum || "").localeCompare(a.datum || "") || (b.created_at || "").localeCompare(a.created_at || "");
      default: // neueste = Erfassungszeitpunkt
        return (b.created_at || "").localeCompare(a.created_at || "") || (b.datum || "").localeCompare(a.datum || "");
    }
  };

  // Entwürfe werden standardmäßig NICHT gezeigt — nur wenn der Entwürfe-Filter aktiv
  // ist (dann ausschließlich Entwürfe). Sonst die abgeschlossenen Belege.
  const gefiltert = useMemo(
    () =>
      (showDrafts ? drafts : rest)
        .filter((b) => matchSeg(b) && matchFilter(b) && matchSuche(b))
        .sort(sortCmp),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [showDrafts, drafts, rest, seg, erfasserSel, projektSel, flags, sortBy, term],
  );

  // Nur bei zeitbasierter Sortierung nach Zeitraum gruppieren; bei Betrag/Status
  // eine flache, klar sortierte Liste (bessere Übersicht fürs Vorverarbeiten).
  const groupByZeitraum = sortBy === "neueste" || sortBy === "datum";
  const buckets = useMemo(() => {
    if (!groupByZeitraum) return [{ label: "", items: gefiltert }];
    const order = ["Diese Woche", "Letzte Woche", "Früher"] as const;
    const map = new Map<string, Beleg[]>();
    gefiltert.forEach((b) => {
      const k = bucketOf(b.created_at || b.datum);
      (map.get(k) ?? map.set(k, []).get(k)!).push(b);
    });
    return order.filter((k) => map.has(k)).map((k) => ({ label: k as string, items: map.get(k)! }));
  }, [gefiltert, groupByZeitraum]);

  // Zähler
  const counts = useMemo(() => ({
    fehlt: rest.filter((b) => typKey(b) !== "lieferschein" && fehltBetrag(b)).length,
    unklass: rest.filter(istUnklassifiziert).length,
    ohne_konto: rest.filter((b) => !hatKonto(b)).length,
    unbezahlt: rest.filter(istUnbezahlt).length,
    mail: alleBelege.filter((b) => b.source === "mail").length,
    entwurf: drafts.length,
  }), [rest, alleBelege, drafts]);

  // Erfasser-Optionen (Scanner + Mails)
  const erfasserOptionen = useMemo(() => {
    const opts = (scannerQ.data?.scanner ?? []).map((s) => ({ key: s.id, label: firstName(s.name), ini: initials(s.name) }));
    if (counts.mail > 0) opts.push({ key: "mail", label: "E-Mail", ini: "@" });
    return opts;
  }, [scannerQ.data, counts.mail]);

  // Projekt-Optionen aus den vorhandenen Belegen
  const projektOptionen = useMemo(() => {
    const m = new Map<string, string>();
    rest.forEach((b) => { if (b.projekt_id) m.set(b.projekt_id, b.projekt_title || "Projekt"); });
    return [...m.entries()].map(([id, title]) => ({ id, title }));
  }, [rest]);

  const activeCount =
    erfasserSel.size + (projektSel !== "alle" ? 1 : 0) + flags.size +
    (sortBy !== "neueste" ? 1 : 0) + (seg !== "alle" ? 1 : 0) + (showDrafts ? 1 : 0);
  const filterAktiv = activeCount > 0 || !!term;

  const toggleFlag = (f: FilterFlag) =>
    setFlags((prev) => {
      const next = new Set(prev);
      next.has(f) ? next.delete(f) : next.add(f);
      return next;
    });

  const reset = () => {
    setErfasserSel(new Set());
    setProjektSel("alle");
    setFlags(new Set());
    setSortBy("neueste");
    setSeg("alle");
    setShowDrafts(false);
  };

  const today = new Intl.DateTimeFormat("de-DE", { weekday: "long", day: "2-digit", month: "long", year: "numeric" }).format(new Date());

  const SEGS: { id: typeof seg; label: string; icon?: IconName; tone?: boolean }[] = [
    { id: "alle", label: "Alle" },
    { id: "beleg", label: "Rechnungen", icon: "receipt" },
    { id: "helfer", label: "Helfer", icon: "person" },
  ];

  const toggleErfasser = (key: string) =>
    setErfasserSel((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  return (
    <div className="sf">
      {/* Kopf */}
      <header className="sf-head">
        <div className="sf-head-tt">
          <div className="sf-date">{today}</div>
          <div className="sf-title">Scan<b>Flow</b></div>
        </div>
        <button className="sf-acct" aria-label="Konto" onClick={() => nav("/profil")}><SFIcon name="account" /></button>
      </header>

      {/* CTA */}
      <div className="sf-cta-wrap">
        <button className="sf-cta" onClick={() => nav("/erfassen")}><SFIcon name="camera" /> Beleg scannen</button>
      </div>

      {/* DATEV-Sammelversand — nur sichtbar, wenn wirklich etwas bereitliegt. */}
      <DatevBatchCard />

      {/* Zahllauf: was noch überwiesen werden muss. */}
      <ZahllaufCard />


      {/* Segmentleiste */}
      <div className="sf-seg">
        {SEGS.map((s) => (
          <button key={s.id} className={"sf-seg-btn" + (seg === s.id ? " is-on" : "") + (s.tone ? " tone-amber" : "")}
            onClick={() => setSeg(s.id)}>
            {s.icon && <SFIcon name={s.icon} />}{s.label}
          </button>
        ))}
      </div>

      {/* Suche + Filter */}
      <div className="sf-sticky" ref={stickyRef}>
        <div className="sf-searchbar">
          <div className="sf-search">
            <SFIcon name="search" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Lieferant, Projekt, Notiz …" autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false} />
            {q && <button className="sf-search-x" onClick={() => setQ("")} aria-label="Leeren"><SFIcon name="x" /></button>}
          </div>
          <button className={"sf-filterbtn" + (filterOpen ? " is-open" : "") + (activeCount > 0 ? " has" : "")}
            onClick={() => setFilterOpen((o) => !o)} aria-label="Filter">
            <SFIcon name="funnel" />
            {activeCount > 0 && <span className="sf-filterbadge">{activeCount}</span>}
          </button>
        </div>

        {filterOpen && (
          <div className="sf-filterpanel">
            {erfasserOptionen.length > 0 && (
              <>
                <div className="sf-rowlabel">Erfasst von</div>
                <div className="sf-chiprow">
                  <button className={"sf-chip" + (erfasserSel.size === 0 ? " is-on" : "")} onClick={() => setErfasserSel(new Set())}>Alle</button>
                  {erfasserOptionen.map((e) => (
                    <button key={e.key} className={"sf-chip" + (erfasserSel.has(e.key) ? " is-on" : "")} onClick={() => toggleErfasser(e.key)}>
                      <span className="sf-chip-av">{e.ini}</span>{e.label}
                      {e.key === "mail" && <span className="cnt">{counts.mail}</span>}
                    </button>
                  ))}
                </div>
              </>
            )}
            {projektOptionen.length > 0 && (
              <>
                <div className="sf-rowlabel">Projekt</div>
                <ProjektFilter
                  options={projektOptionen}
                  value={projektSel}
                  onChange={setProjektSel}
                />
              </>
            )}
            <div className="sf-rowlabel">Sortieren nach</div>
            <div className="sf-chiprow is-tight is-wrap">
              {([
                { key: "neueste", label: "Neueste" },
                { key: "datum", label: "Rechnungsdatum" },
                { key: "betrag", label: "Betrag" },
                { key: "status", label: "Status" },
              ] as const).map((s) => (
                <button key={s.key} className={"sf-chip" + (sortBy === s.key ? " is-on" : "")}
                  onClick={() => setSortBy(s.key)}>{s.label}</button>
              ))}
            </div>
            <div className="sf-rowlabel">Filter</div>
            <div className="sf-quick">
              <button className={"sf-chip" + (flags.has("ohne_konto") ? " is-on" : "")} onClick={() => toggleFlag("ohne_konto")}>
                Ohne Kontozuordnung <span className="cnt">{counts.ohne_konto}</span>
              </button>
              <button className={"sf-chip" + (flags.has("unbezahlt") ? " is-on" : "")} onClick={() => toggleFlag("unbezahlt")}>
                Unbezahlt <span className="cnt">{counts.unbezahlt}</span>
              </button>
              <button className={"sf-chip warn" + (flags.has("fehlt") ? " is-on" : "")} onClick={() => toggleFlag("fehlt")}>
                Betrag fehlt <span className="cnt">{counts.fehlt}</span>
              </button>
              <button className={"sf-chip" + (flags.has("unklass") ? " is-on" : "")} onClick={() => toggleFlag("unklass")}>
                Unklassifiziert <span className="cnt">{counts.unklass}</span>
              </button>
              {counts.entwurf > 0 && (
                <button className={"sf-chip" + (showDrafts ? " is-on" : "")} onClick={() => setShowDrafts((v) => !v)}>
                  Entwürfe <span className="cnt">{counts.entwurf}</span>
                </button>
              )}
            </div>
            {filterAktiv && (
              <button className="sf-clearall" onClick={reset}>Alle Filter zurücksetzen</button>
            )}
          </div>
        )}
      </div>

      {/* Liste */}
      <div className="sf-scroll">
        {belegeQ.isLoading && <div className="sf-empty">Lade Rechnungen…</div>}
        {belegeQ.isError && <div className="sf-empty" style={{ color: "tomato" }}>{belegeQ.error?.message}</div>}

        {/* Entwürfe stehen bewusst nicht in der Hauptliste — sie sind aber
            Arbeit, die auf jemanden wartet, und ihre einzige Tür saß bis zum
            19.08.2026 hinter dem zugeklappten Filter. Wer einen Beleg dort
            nicht fand, hielt ihn für verschwunden (Benjamin, Helferrechnung
            2026-014). Deshalb hier ein Hinweis, den man nicht übersehen kann. */}
        {!belegeQ.isLoading && !showDrafts && frisch.length > 0 && (
          <>
            <div className="sf-day" style={{ color: "var(--accent)" }}>
              Gerade gescannt <span className="c">{frisch.length}</span>
            </div>
            <div className="sf-tl" style={{ marginBottom: 14 }}>
              {frisch.map((b) => (
                <BelegRow key={b.id} b={b} name={anzeigeName(b)} erfIni={erfasserIni(erfasserKey(b))} draft onOpen={() => nav(`/beleg/${b.id}`)} />
              ))}
            </div>
          </>
        )}
        {!belegeQ.isLoading && !showDrafts && counts.entwurf - frisch.length > 0 && (
          <button className="sf-entwurfbanner" onClick={() => setShowDrafts(true)}>
            <span>
              <b>{counts.entwurf - frisch.length}</b>{" "}
              {counts.entwurf - frisch.length === 1
                ? frisch.length ? "weiterer Beleg ist noch nicht fertig" : "Beleg ist noch nicht fertig"
                : frisch.length ? "weitere Belege sind noch nicht fertig" : "Belege sind noch nicht fertig"}
            </span>
            <span className="sf-entwurfbanner-cta">anzeigen ›</span>
          </button>
        )}

        {!belegeQ.isLoading && gefiltert.length === 0 && !(frisch.length && !showDrafts && !filterAktiv) && (
          <div className="sf-empty">
            {showDrafts
              ? "Keine Entwürfe."
              : belegeQ.sucheLaeuft
                ? "Suche in allen Belegen…"
                : filterAktiv
                ? "Kein Beleg passt zu Suche/Filter."
                : "Noch keine Belege erfasst. Oben auf „Beleg scannen“ tippen."}
          </div>
        )}

        {/* Entwürfe-Filter aktiv → flache Liste unter einer Überschrift; sonst nach
            Zeitraum gruppiert. */}
        {showDrafts
          ? gefiltert.length > 0 && (
              <>
                <div className="sf-day" style={{ color: "var(--accent)" }}>
                  Entwürfe — abschließen <span className="c">{gefiltert.length}</span>
                </div>
                <div className="sf-tl">
                  {gefiltert.map((b) => (
                    <BelegRow key={b.id} b={b} name={anzeigeName(b)} erfIni={erfasserIni(erfasserKey(b))} draft onOpen={() => nav(`/beleg/${b.id}`)} />
                  ))}
                </div>
              </>
            )
          : buckets.map((g) => (
              <div key={g.label || "flat"}>
                <div className="sf-day">
                  {g.label || (sortBy === "betrag" ? "Nach Betrag" : "Nach Status")}{" "}
                  <span className="c">{g.items.length}</span>
                </div>
                <div className="sf-tl">
                  {g.items.map((b) => (
                    <BelegRow key={b.id} b={b} name={anzeigeName(b)} erfIni={erfasserIni(erfasserKey(b))} onOpen={() => nav(`/beleg/${b.id}`)} />
                  ))}
                </div>
              </div>
            ))}
        {!belegeQ.isLoading && (
          <ListenEnde
            geladen={belegeQ.geladen}
            gesamt={belegeQ.gesamt}
            hatMehr={belegeQ.hatMehr}
            laedtMehr={belegeQ.laedtMehr}
            mehrLaden={belegeQ.mehrLaden}
            serverSuche={belegeQ.serverSuche}
            suchGekappt={belegeQ.suchGekappt}
            suchTreffer={belegeQ.suchTreffer}
            angezeigt={alleBelege.length}
          />
        )}
      </div>
    </div>
  );
}

function BelegRow({ b, name, erfIni, draft, onOpen }: { b: Beleg; name: string; erfIni: string; draft?: boolean; onOpen: () => void }) {
  const typ = typKey(b);
  const meta = TYP_META[typ];
  // Startseite zeigt den Bruttobetrag (Kontozuordnung); Netto nur als Fallback.
  const betrag = (b.bruttobetrag ?? 0) > 0 ? b.bruttobetrag : (b.netto ?? 0) > 0 ? b.netto : null;

  let amt: React.ReactNode;
  if (b.ki_laeuft) {
    amt = <span className="sf-tl-amt sf-clara">Clara liest …</span>;
  } else if (typ === "lieferschein") {
    amt = <span className="sf-tl-amt" style={{ color: "var(--m-ink-3)", fontSize: 12.5, fontWeight: 650 }}>Lieferschein</span>;
  } else if (betrag != null) {
    amt = <span className="sf-tl-amt">{fmtEuro(betrag)}</span>;
  } else {
    amt = <span className="sf-tl-amt miss">Betrag fehlt</span>;
  }

  return (
    <button className={"sf-tl-row tone-" + meta.tone} onClick={onOpen}>
      <div className="sf-thumb sm sf-tl-thumb">
        <img src={api.thumbnailUrl(b.id, 160)} alt="" onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }} />
      </div>
      <div className="sf-tl-tt">
        <div className="sf-tl-name">{name}</div>
        <div className="sf-tl-sub">
          <span className={"sf-type tone-" + meta.tone}><SFIcon name={meta.icon} />{meta.kurz}</span>
          {b.ki_laeuft
            ? b.projekt_title || "Wird gelesen"
            : draft
              ? "Entwurf · tippen zum Abschließen"
              : b.projekt_title || "Ohne Projekt"}
        </div>
      </div>
      <div className="sf-tl-right">
        {amt}
        <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span className="sf-mini-av">{erfIni}</span>
          {!draft && <KontoBadge b={b} />}
        </span>
      </div>
    </button>
  );
}

/** DATEV-Sammelversand: zeigt, wie viele Rechnungen versandbereit sind, und schickt sie
 *  nach ausdrücklicher Bestätigung raus. Nie automatisch — Geld-/Steuerkommunikation
 *  wird immer bewusst ausgelöst. */
function DatevBatchCard() {
  const qc = useQueryClient();
  const [result, setResult] = useState<DatevSendResult | null>(null);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [sendError, setSendError] = useState<string | null>(null);

  const pendingQ = useQuery({
    queryKey: ["datev-pending"],
    queryFn: () => api.datevPending(),
    staleTime: 60 * 1000,
  });

  const items = pendingQ.data ?? [];
  const summe = items.reduce((s, i) => s + (i.betrag || 0), 0);

  /** In kleinen Paketen senden — so sieht der Nutzer echten Fortschritt statt einer
   *  minutenlang stehenden „Sende…"-Anzeige (54 Belege ≈ 80 s). */
  async function sendeAlle() {
    const ids = items.map((i) => i.beleg_id);
    setRunning(true);
    setSendError(null);
    setProgress({ done: 0, total: ids.length });
    const acc: DatevSendResult = { gesendet: 0, uebersprungen: 0, fehler: [], to_email: "" };
    const CHUNK = 5;
    try {
      for (let i = 0; i < ids.length; i += CHUNK) {
        const r = await api.datevSendBatch(ids.slice(i, i + CHUNK));
        acc.gesendet += r.gesendet;
        acc.uebersprungen += r.uebersprungen;
        acc.fehler = [...acc.fehler, ...(r.fehler ?? [])];
        acc.to_email = r.to_email || acc.to_email;
        setProgress({ done: Math.min(i + CHUNK, ids.length), total: ids.length });
      }
      setResult(acc);
    } catch (e) {
      setSendError((e as Error).message);
    } finally {
      setRunning(false);
      qc.invalidateQueries({ queryKey: ["datev-pending"] });
      qc.invalidateQueries({ queryKey: ["belege-recent"] });
    }
  }

  const box: React.CSSProperties = {
    margin: "0 20px 8px",
    padding: "11px 14px",
    borderRadius: 12,
    background: "var(--bg-2, #1a1a1a)",
    border: "1px solid var(--line, #333)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  };

  if (result) {
    const fehler = result.fehler?.length ?? 0;
    return (
      <div style={{ ...box, display: "block" }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text, #eee)" }}>
          {result.gesendet} an DATEV gesendet
          {fehler > 0 && ` · ${fehler} nicht gesendet`}
        </div>
        {fehler > 0 && (
          <ul style={{ margin: "6px 0 0", paddingLeft: 18, fontSize: 11.5, color: "var(--text-3, #999)" }}>
            {result.fehler.slice(0, 5).map((f) => (
              <li key={f.beleg_id}>{f.lieferant}: {f.grund}</li>
            ))}
          </ul>
        )}
        <button
          onClick={() => setResult(null)}
          style={{
            marginTop: 10, padding: "8px 14px", borderRadius: 9, border: "1px solid var(--line, #333)",
            background: "transparent", color: "var(--text-2, #bbb)", fontSize: 12.5, fontWeight: 600,
          }}
        >
          OK
        </button>
      </div>
    );
  }

  // Läuft gerade: Fortschritt statt starrem „Sende…" — mit Balken und Zähler,
  // damit klar ist, dass der Vorgang lebt.
  if (running) {
    const pct = progress.total ? Math.round((progress.done / progress.total) * 100) : 0;
    return (
      <div style={{ ...box, display: "block" }}>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "var(--text, #eee)" }}>
            Sende an DATEV…
          </span>
          <span style={{ fontSize: 12, color: "var(--text-3, #999)" }}>
            {progress.done} von {progress.total}
          </span>
        </div>
        <div className="sf-progress" style={{ marginTop: 8 }}>
          <i style={{ width: `${pct}%` }} />
        </div>
        <div style={{ fontSize: 11, color: "var(--text-3, #999)", marginTop: 6 }}>
          Bitte offen lassen, bis alle Rechnungen raus sind.
        </div>
      </div>
    );
  }

  if (pendingQ.isLoading || items.length === 0) return null;

  return (
    <div style={box}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text, #eee)" }}>
          {items.length} {items.length === 1 ? "Rechnung" : "Rechnungen"} bereit für DATEV
        </div>
        <div style={{ fontSize: 11.5, color: "var(--text-3, #999)", marginTop: 1 }}>
          {fmtEuro(summe)} · noch nicht gesendet
        </div>
        {sendError && (
          <div style={{ fontSize: 12, color: "tomato", marginTop: 4 }}>{sendError}</div>
        )}
      </div>
      <button
        onClick={() => {
          const ok = window.confirm(
            `${items.length} Rechnungen (${fmtEuro(summe)}) jetzt an DATEV senden?`,
          );
          if (ok) void sendeAlle();
        }}
        style={{
          flexShrink: 0, padding: "9px 16px", borderRadius: 10, border: "none",
          background: "var(--accent, #c14a3d)", color: "#fff", fontSize: 13, fontWeight: 700,
        }}
      >
        Senden
      </button>
    </div>
  );
}

/** Zahllauf (Überweisungskorb): was noch bezahlt werden muss. Rein informativ —
 *  die SEPA-Überweisung selbst läuft am Desktop (pushTAN). Ein Beleg landet hier
 *  nur, wenn er beim Verarbeiten explizit „in den Zahllauf übernommen" wurde. */
function ZahllaufCard() {
  const zQ = useQuery({
    queryKey: ["zahllauf"],
    queryFn: () => api.zahllauf(),
    staleTime: 60 * 1000,
  });

  const z = zQ.data;
  if (!z) return null;
  const gesamt = z.zahlbereit.length + z.fast_zahlbereit.length + z.klaerfall.length;
  if (gesamt === 0) return null;

  const zeile = (farbe: string, text: string) => (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, whiteSpace: "nowrap" }}>
      <span style={{ width: 7, height: 7, borderRadius: 99, background: farbe, display: "inline-block" }} />
      {text}
    </span>
  );

  return (
    <div
      style={{
        margin: "0 20px 8px",
        padding: "11px 14px",
        borderRadius: 12,
        background: "var(--bg-2, #1a1a1a)",
        border: "1px solid var(--line, #333)",
      }}
    >
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: "var(--text, #eee)" }}>
          Ausgangskorb · {gesamt} warten
        </span>
        <span style={{ fontSize: 12.5, fontWeight: 700, color: "var(--text, #eee)" }}>
          {fmtEuro(z.summe_gesamt)}
        </span>
      </div>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 10,
          marginTop: 6,
          fontSize: 11.5,
          color: "var(--text-3, #999)",
        }}
      >
        {z.zahlbereit.length > 0 &&
          zeile("#3fae7a", `${z.zahlbereit.length} angehalten — im Kontor prüfen · ${fmtEuro(z.summe_zahlbereit)}`)}
        {z.fast_zahlbereit.length > 0 && zeile("#e0a53f", `${z.fast_zahlbereit.length}× IBAN übernehmen`)}
        {z.klaerfall.length > 0 && zeile("var(--text-3, #999)", `${z.klaerfall.length} unklar`)}
      </div>
      <div style={{ fontSize: 11, color: "var(--text-3, #999)", marginTop: 6 }}>
        Was zahlbereit ist, geht mit der nächsten Bank-Freigabe raus.
      </div>
    </div>
  );
}

/** Projekt-Filter als durchsuchbares Autocomplete (statt langer Chip-Liste).
 *  Benjamins stehende UI-Präferenz: Auswahl aus vielen Optionen = Combobox. */
function ProjektFilter({
  options,
  value,
  onChange,
}: {
  options: { id: string; title: string }[];
  value: string;
  onChange: (v: string) => void;
}) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const selected = value !== "alle" ? options.find((o) => o.id === value) : null;
  const term = query.trim().toLowerCase();
  const gefiltert = (term ? options.filter((o) => o.title.toLowerCase().includes(term)) : options).slice(0, 8);

  const inputStyle: React.CSSProperties = {
    width: "100%",
    boxSizing: "border-box",
    padding: "9px 12px",
    borderRadius: 10,
    border: "1px solid var(--m-line, #d8d8d8)",
    background: "var(--m-card, #fff)",
    color: "var(--m-ink, #1a1a1a)",
    fontSize: 14,
  };

  if (selected) {
    return (
      <div className="sf-chiprow is-tight" style={{ marginBottom: 8 }}>
        <button className="sf-chip is-on" onClick={() => { onChange("alle"); setQuery(""); }}>
          {selected.title} <span style={{ marginLeft: 4, opacity: 0.8 }}>✕</span>
        </button>
      </div>
    );
  }

  return (
    <div style={{ position: "relative", marginBottom: 8 }}>
      <input
        value={query}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder="Projekt suchen…"
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck={false}
        style={inputStyle}
      />
      {open && gefiltert.length > 0 && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            right: 0,
            zIndex: 20,
            maxHeight: 240,
            overflowY: "auto",
            borderRadius: 10,
            border: "1px solid var(--m-line, #d8d8d8)",
            background: "var(--m-card, #fff)",
            boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
          }}
        >
          {gefiltert.map((o) => (
            <button
              key={o.id}
              onMouseDown={() => { onChange(o.id); setQuery(""); setOpen(false); }}
              style={{
                display: "block",
                width: "100%",
                textAlign: "left",
                padding: "10px 12px",
                border: "none",
                borderBottom: "1px solid var(--m-line, #ededed)",
                background: "transparent",
                color: "var(--m-ink, #1a1a1a)",
                fontSize: 13,
                cursor: "pointer",
              }}
            >
              {o.title}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Kompakter Kontozuordnungs-Indikator in der Belegzeile — ersetzt den früher
 *  unklaren Status-Punkt. Grün „Konto" = einer Bankbuchung zugeordnet, „Bar" bzw.
 *  „Privat" = braucht keine, grau „offen" = noch keiner Buchung zugeordnet. */
function KontoBadge({ b }: { b: Beleg }) {
  const zugeordnet = hatKonto(b);
  const label = !zugeordnet ? "offen" : b.kontobewegung_id ? "Konto" : b.zahlungsart === "bar" ? "Bar" : "Privat";
  return (
    <span
      title={!zugeordnet ? "Noch keiner Bankbuchung zugeordnet"
        : b.kontobewegung_id ? "Kontozuordnung vorhanden" : "Aus eigener Tasche bezahlt — keine Bankzeile der GbR"}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        fontSize: 10,
        fontWeight: 700,
        padding: "3px 7px",
        borderRadius: 999,
        border: `1px solid ${zugeordnet ? "var(--green)" : "var(--m-line, #d8d8d8)"}`,
        background: zugeordnet ? "rgba(63,174,122,0.14)" : "transparent",
        color: zugeordnet ? "var(--green)" : "var(--m-ink-3, #8a8a8a)",
        whiteSpace: "nowrap",
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: 999,
          display: "inline-block",
          background: zugeordnet ? "var(--green)" : "transparent",
          border: zugeordnet ? "none" : "1.5px solid currentColor",
        }}
      />
      {label}
    </span>
  );
}
